import { useCallback, useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal, In, Sel } from "./Projects";
import ReceiptViewer from "@/components/ReceiptViewer";
import { Truck, Plus, Fuel, Paperclip, CheckCircle2, XCircle, Trash2, Pencil, ChevronLeft, ChevronRight, Loader2, Gauge, BarChart3, ScanLine } from "lucide-react";
import { toast } from "sonner";

const FLEET_TYPES = [
  { value: "gasolina", label: "Gasolina" },
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "llantas", label: "Llantas" },
  { value: "casetas", label: "Casetas" },
  { value: "verificacion", label: "Verificación" },
  { value: "seguro", label: "Seguro" },
  { value: "tenencia", label: "Tenencia" },
  { value: "otros", label: "Otros" },
];
const typeLabel = (v) => FLEET_TYPES.find((t) => t.value === v)?.label || v;
const STATUS_STYLE = {
  pendiente: { background: "rgba(251,191,36,0.12)", color: "#fbbf24" },
  aprobado: { background: "rgba(34,197,94,0.12)", color: "var(--green)" },
  rechazado: { background: "rgba(239,68,68,0.12)", color: "var(--red)" },
};
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const monthLabel = (ym) => `${MONTHS_ES[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`;
const shiftMonth = (ym, delta) => {
  const d = new Date(parseInt(ym.slice(0, 4), 10), parseInt(ym.slice(5, 7), 10) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Fleet() {
  const { user } = useAuth();
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canApprove = ["admin", "contador"].includes(user?.role);
  const [vehicles, setVehicles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // vehicle id
  const [statement, setStatement] = useState(null);
  const [openVehicle, setOpenVehicle] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [openExpense, setOpenExpense] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [view, setView] = useState("grid"); // grid | compare

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p] = await Promise.all([api.get("/fleet/vehicles"), api.get("/projects")]);
      setVehicles(v.data);
      setProjects(p.data);
    } catch {
      toast.error("No se pudieron cargar los vehículos");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadStatement = useCallback(async (vid) => {
    try {
      const { data } = await api.get(`/fleet/vehicles/${vid}/statement`);
      setStatement(data);
    } catch {
      toast.error("No se pudo cargar el estado de cuenta");
    }
  }, []);
  useEffect(() => { if (selected) loadStatement(selected); else setStatement(null); }, [selected, loadStatement]);

  const projName = (pid) => projects.find((p) => p.id === pid)?.name || null;

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      if (selected) loadStatement(selected);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-5" data-testid="fleet-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">// VEHÍCULOS DE OBRA</div>
          <h1 className="font-display font-bold text-slate-100 text-4xl sm:text-5xl lg:text-6xl" style={{ fontSize: "1.75rem" }}>Flotilla</h1>
        </div>
        <div className="flex items-center gap-2">
          {selected && (
            <button onClick={() => setSelected(null)} data-testid="fleet-back-btn" className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-1.5">
              <ChevronLeft className="w-4 h-4" /> Flotilla
            </button>
          )}
          {selected && (
            <button onClick={() => setOpenExpense(true)} data-testid="fleet-new-expense-btn" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-2">
              <Fuel className="w-4 h-4" /> Registrar gasto
            </button>
          )}
          {!selected && canManage && (
            <>
              <button onClick={() => setView(view === "compare" ? "grid" : "compare")} data-testid="fleet-compare-btn"
                className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-2 border" style={view === "compare" ? { borderColor: "var(--cyan)", color: "var(--cyan)" } : { borderColor: "var(--border)" }}>
                <BarChart3 className="w-4 h-4" /> Comparativa
              </button>
              <button onClick={() => { setEditVehicle(null); setOpenVehicle(true); }} data-testid="fleet-new-vehicle-btn" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-2">
                <Plus className="w-4 h-4" /> Vehículo
              </button>
            </>
          )}
        </div>
      </div>

      {!selected ? (
        view === "compare" && canManage ? (
          <FleetComparison projects={projects} onSelect={(vid) => { setView("grid"); setSelected(vid); }} />
        ) : loading ? (
          <div className="panel p-8 text-center text-slate-400">Cargando…</div>
        ) : vehicles.length === 0 ? (
          <div className="panel p-12 text-center text-slate-500" data-testid="fleet-empty">
            <Truck className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            Sin vehículos registrados. {canManage ? "Agrega el primero con el botón Vehículo." : ""}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((v) => (
              <button key={v.id} onClick={() => setSelected(v.id)} data-testid={`fleet-vehicle-${v.id}`}
                className="panel p-4 text-left transition-transform hover:-translate-y-0.5" style={{ opacity: v.active === false ? 0.5 : 1 }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.3)" }}>
                      <Truck className="w-5 h-5" style={{ color: "var(--cyan)" }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-slate-100 truncate">{v.plates}{v.active === false && <span className="text-[9px] ml-2 text-slate-500">INACTIVO</span>}</div>
                      <div className="text-xs text-slate-400 truncate">{[v.brand, v.model, v.year].filter(Boolean).join(" ") || v.name || "—"}</div>
                    </div>
                  </div>
                  {v.pending_count > 0 && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono shrink-0" style={STATUS_STYLE.pendiente} data-testid={`fleet-pending-${v.id}`}>{v.pending_count} por aprobar</span>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <div className="text-slate-500 truncate">
                    {v.driver_name && <div>🧑‍🔧 {v.driver_name}</div>}
                    <div className="truncate">{projName(v.project_id) ? `🏗 ${projName(v.project_id)}` : "Administrativo"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="label-mono text-[8px]">GASTO DEL MES</div>
                    <div className="font-mono font-bold" style={{ color: "var(--cyan)" }}>{fmtMXN(v.month_total)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      ) : statement ? (
        <VehicleDetail statement={statement} projects={projects} canManage={canManage} canApprove={canApprove}
          onEdit={() => { setEditVehicle(statement.vehicle); setOpenVehicle(true); }}
          onApprove={(id) => act(() => api.post(`/fleet/expenses/${id}/approve`), "Gasto aprobado y registrado en la obra/caja")}
          onReject={(id) => act(() => api.post(`/fleet/expenses/${id}/reject`), "Gasto rechazado")}
          onDelete={(id) => { if (window.confirm("¿Eliminar este gasto? También se elimina el gasto vinculado en contabilidad.")) act(() => api.delete(`/fleet/expenses/${id}`), "Gasto eliminado"); }}
          onViewTicket={(fx) => setViewer({ fileId: fx.receipt_path, title: `${fx.vehicle_plates} · ${typeLabel(fx.expense_type)}` })}
        />
      ) : (
        <div className="panel p-8 text-center text-slate-400">Cargando…</div>
      )}

      {openVehicle && (
        <VehicleModal vehicle={editVehicle} projects={projects}
          onClose={() => setOpenVehicle(false)}
          onDone={() => { setOpenVehicle(false); load(); if (selected) loadStatement(selected); }} />
      )}
      {openExpense && selected && (
        <FleetExpenseModal vehicle={statement?.vehicle} onClose={() => setOpenExpense(false)}
          onDone={() => { setOpenExpense(false); loadStatement(selected); load(); }} />
      )}
      {viewer && <ReceiptViewer fileId={viewer.fileId} title={viewer.title} onClose={() => setViewer(null)} />}
    </div>
  );
}

function FleetComparison({ projects, onSelect }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/fleet/comparison", { params: { month } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudo cargar la comparativa"));
  }, [month]);

  if (!data) return <div className="panel p-8 text-center text-slate-400">Cargando…</div>;
  const OTHER = ["verificacion", "seguro", "tenencia", "otros"];
  const sumOther = (bt) => OTHER.reduce((s, t) => s + (bt[t] || 0), 0);
  const maxTotal = Math.max(...data.rows.map((r) => r.total), 0);
  const projName = (pid) => projects.find((p) => p.id === pid)?.name || "Administrativo";
  const delta = (r) => (r.prev_total > 0 ? ((r.total - r.prev_total) / r.prev_total) * 100 : null);

  return (
    <div className="space-y-3" data-testid="fleet-comparison">
      <div className="panel p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono" style={{ color: "var(--magenta)" }}>// COMPARATIVA MENSUAL — ¿CUÁL SALE MÁS CARO?</div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="comp-prev" className="btn-ghost p-1.5 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-display font-bold text-slate-100 w-40 text-center" data-testid="comp-month">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="comp-next" className="btn-ghost p-1.5 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="fleet-comparison-table">
            <thead className="panel-header">
              <tr className="text-left">
                <th className="label-mono px-3 py-2.5">Vehículo</th>
                <th className="label-mono px-3 py-2.5 text-right">Gasolina</th>
                <th className="label-mono px-3 py-2.5 text-right">Mantenim.</th>
                <th className="label-mono px-3 py-2.5 text-right">Llantas</th>
                <th className="label-mono px-3 py-2.5 text-right">Casetas</th>
                <th className="label-mono px-3 py-2.5 text-right">Otros</th>
                <th className="label-mono px-3 py-2.5 text-right">Total mes</th>
                <th className="label-mono px-3 py-2.5 text-right">vs mes ant.</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">Sin vehículos registrados.</td></tr>
              )}
              {data.rows.map((r, idx) => {
                const d = delta(r);
                const top = idx === 0 && r.total > 0;
                return (
                  <tr key={r.vehicle_id} data-testid={`comp-row-${r.vehicle_id}`} onClick={() => onSelect(r.vehicle_id)}
                    className="row-hover cursor-pointer" style={{ borderBottom: "1px solid var(--border)", background: top ? "rgba(239,68,68,0.06)" : undefined }}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {top && <span title="El más caro del mes" className="text-[10px]" style={{ color: "var(--red)" }}>▲</span>}
                        <div>
                          <div className="font-semibold text-slate-100">{r.plates}{r.active === false && <span className="text-[9px] ml-2 text-slate-500">INACTIVO</span>}</div>
                          <div className="text-[10px] text-slate-500">{[r.brand, r.model].filter(Boolean).join(" ")} · {projName(r.project_id)}</div>
                        </div>
                      </div>
                      {maxTotal > 0 && (
                        <div className="mt-1.5 h-1 rounded-full" style={{ background: "var(--bg-2)", maxWidth: 180 }}>
                          <div className="h-1 rounded-full" style={{ width: `${(r.total / maxTotal) * 100}%`, background: top ? "var(--red)" : "var(--cyan)" }} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{r.by_type.gasolina ? fmtMXN(r.by_type.gasolina) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{r.by_type.mantenimiento ? fmtMXN(r.by_type.mantenimiento) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{r.by_type.llantas ? fmtMXN(r.by_type.llantas) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{r.by_type.casetas ? fmtMXN(r.by_type.casetas) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{sumOther(r.by_type) ? fmtMXN(sumOther(r.by_type)) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: top ? "var(--red)" : "var(--cyan)" }}>{fmtMXN(r.total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">
                      {d === null ? <span className="text-slate-600">—</span> : (
                        <span style={{ color: d > 0 ? "var(--red)" : "var(--green)" }}>{d > 0 ? "+" : ""}{d.toFixed(0)}%</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr style={{ background: "var(--bg-2)", borderTop: "1px solid var(--border-2)" }}>
                  <td className="px-3 py-2.5 font-semibold text-slate-200 text-xs uppercase tracking-widest">Total flotilla</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{fmtMXN(data.totals.by_type.gasolina || 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{fmtMXN(data.totals.by_type.mantenimiento || 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{fmtMXN(data.totals.by_type.llantas || 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{fmtMXN(data.totals.by_type.casetas || 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-300">{fmtMXN(sumOther(data.totals.by_type))}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: "var(--cyan)" }} data-testid="comp-total">{fmtMXN(data.totals.total)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500">{data.totals.prev_total > 0 ? `ant: ${fmtMXN(data.totals.prev_total)}` : "—"}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <p className="text-[10px] text-slate-500">Solo gastos aprobados. "Otros" agrupa verificación, seguro, tenencia y otros. Clic en un vehículo para abrir su estado de cuenta.</p>
    </div>
  );
}

function VehicleDetail({ statement, projects, canManage, canApprove, onEdit, onApprove, onReject, onDelete, onViewTicket }) {
  const v = statement.vehicle;
  const proj = projects.find((p) => p.id === v.project_id);
  return (
    <div className="space-y-4" data-testid="fleet-detail">
      <div className="panel p-4 flex flex-wrap items-center gap-4">
        <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.3)" }}>
          <Truck className="w-6 h-6" style={{ color: "var(--cyan)" }} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-bold text-xl text-slate-100">{v.plates} {v.name && <span className="text-sm text-slate-400 font-normal">· {v.name}</span>}</div>
          <div className="text-xs text-slate-400">{[v.brand, v.model, v.year].filter(Boolean).join(" ")}{v.vin ? ` · Serie: ${v.vin}` : ""}{v.driver_name ? ` · Chofer: ${v.driver_name}` : ""} · {proj ? `Obra: ${proj.name}` : "Administrativo"}</div>
        </div>
        {canManage && (
          <button onClick={onEdit} data-testid="fleet-edit-vehicle-btn" className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBox label="TOTAL HISTÓRICO" value={fmtMXN(statement.total)} color="var(--cyan)" testId="fleet-stat-total" />
        <StatBox label="GASTO DEL MES" value={fmtMXN(statement.month_total)} color="var(--magenta)" testId="fleet-stat-month" />
        <StatBox label="PENDIENTE APROBAR" value={fmtMXN(statement.pending_total)} color="#fbbf24" testId="fleet-stat-pending" />
        <StatBox label="RENDIMIENTO" value={statement.fuel.avg_kml ? `${statement.fuel.avg_kml} km/L` : "—"} color="#a78bfa" icon={Gauge}
          hint={statement.fuel.avg_kml ? `${statement.fuel.samples} carga${statement.fuel.samples === 1 ? "" : "s"} comparadas` : "Captura km y litros al cargar gasolina"} testId="fleet-stat-fuel" />
      </div>

      {Object.keys(statement.by_type).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="fleet-by-type">
          {Object.entries(statement.by_type).sort((a, b) => b[1] - a[1]).map(([t, amt]) => (
            <span key={t} className="px-2.5 py-1.5 rounded-sm text-xs font-mono" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", color: "var(--text-dim)" }}>
              {typeLabel(t)}: <b style={{ color: "var(--cyan)" }}>{fmtMXN(amt)}</b>
            </span>
          ))}
        </div>
      )}

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="fleet-expenses-table">
            <thead className="panel-header">
              <tr className="text-left">
                <th className="label-mono px-3 py-2.5">Fecha</th>
                <th className="label-mono px-3 py-2.5">Tipo</th>
                <th className="label-mono px-3 py-2.5">Detalle</th>
                <th className="label-mono px-3 py-2.5 text-right">KM / Litros</th>
                <th className="label-mono px-3 py-2.5 text-right">Monto</th>
                <th className="label-mono px-3 py-2.5">Estado</th>
                <th className="label-mono px-3 py-2.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {statement.expenses.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">Sin gastos registrados para este vehículo.</td></tr>
              )}
              {statement.expenses.map((fx) => (
                <tr key={fx.id} data-testid={`fleet-expense-${fx.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-300">{fmtDate(fx.expense_date)}</td>
                  <td className="px-3 py-2.5 text-slate-200">{typeLabel(fx.expense_type)}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-400 max-w-[260px]">
                    <div className="truncate">{fx.notes || "—"}</div>
                    <div className="text-[10px] text-slate-600">{fx.created_by_name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-400">
                    {fx.odometer_km ? `${Number(fx.odometer_km).toLocaleString()} km` : "—"}{fx.liters ? ` / ${fx.liters} L` : ""}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-100">{fmtMXN(fx.amount)}</td>
                  <td className="px-3 py-2.5">
                    <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase" style={STATUS_STYLE[fx.status]}>{fx.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {fx.receipt_path && (
                      <button onClick={() => onViewTicket(fx)} data-testid={`fleet-ticket-${fx.id}`} className="text-slate-400 hover:text-cyan-300 mr-2" title="Ver ticket">
                        <Paperclip className="w-4 h-4 inline" />
                      </button>
                    )}
                    {canApprove && fx.status === "pendiente" && (
                      <>
                        <button onClick={() => onApprove(fx.id)} data-testid={`fleet-approve-${fx.id}`} className="mr-2" title="Aprobar" style={{ color: "var(--green)" }}>
                          <CheckCircle2 className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => onReject(fx.id)} data-testid={`fleet-reject-${fx.id}`} className="mr-2" title="Rechazar" style={{ color: "#fbbf24" }}>
                          <XCircle className="w-4 h-4 inline" />
                        </button>
                      </>
                    )}
                    {canApprove && (
                      <button onClick={() => onDelete(fx.id)} data-testid={`fleet-delete-${fx.id}`} title="Eliminar" style={{ color: "var(--red)" }}>
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ label, value, color, hint, testId }) => (
  <div className="panel px-4 py-3" data-testid={testId}>
    <div className="label-mono text-[9px]">{label}</div>
    <div className="font-mono font-bold text-xl mt-1" style={{ color }}>{value}</div>
    {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
  </div>
);

function VehicleModal({ vehicle, projects, onClose, onDone }) {
  const [f, setF] = useState({
    plates: vehicle?.plates || "", name: vehicle?.name || "", brand: vehicle?.brand || "",
    model: vehicle?.model || "", year: vehicle?.year || "", vin: vehicle?.vin || "",
    driver_name: vehicle?.driver_name || "", project_id: vehicle?.project_id || "", active: vehicle?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  const scanCard = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setScanning(true);
    setDuplicate(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fleet/vehicles/scan-card", fd, { timeout: 120000 });
      const d = data.data || {};
      setF((prev) => ({
        ...prev,
        plates: d.plates || prev.plates, brand: d.brand || prev.brand,
        model: d.model || prev.model, year: d.year || prev.year, vin: d.vin || prev.vin,
      }));
      if (data.existing_vehicle) {
        setDuplicate(data.existing_vehicle);
        toast.error("Este vehículo YA está registrado en la flotilla");
      } else {
        toast.success("Tarjetón leído — revisa los datos y guarda");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo leer el tarjetón");
    } finally {
      setScanning(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f, year: f.year ? Number(f.year) : null, project_id: f.project_id || null, vin: f.vin || null };
      if (vehicle) await api.patch(`/fleet/vehicles/${vehicle.id}`, payload);
      else await api.post("/fleet/vehicles", payload);
      toast.success(vehicle ? "Vehículo actualizado" : "Vehículo registrado");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={vehicle ? "Editar vehículo" : "Nuevo vehículo"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="fleet-vehicle-modal">
        <label className="rounded-sm p-3 flex items-center gap-3 cursor-pointer transition-colors" data-testid="fleet-scan-label"
          style={{ background: "rgba(217,70,239,0.06)", border: "1px dashed rgba(217,70,239,0.45)" }}>
          <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="fleet-scan-input" onChange={scanCard} disabled={scanning} />
          {scanning ? <Loader2 className="w-5 h-5 animate-spin shrink-0" style={{ color: "var(--magenta)" }} /> : <ScanLine className="w-5 h-5 shrink-0" style={{ color: "var(--magenta)" }} />}
          <div className="text-xs">
            <div className="font-semibold" style={{ color: "var(--magenta)" }}>{scanning ? "Leyendo tarjetón con IA…" : "Escanear tarjeta de circulación (IA)"}</div>
            <div className="text-slate-500 text-[11px]">Foto o PDF del tarjetón — llena placas, marca, modelo, año y número de serie automáticamente</div>
          </div>
        </label>
        {duplicate && (
          <div className="rounded-sm p-2.5 text-xs" data-testid="fleet-duplicate-warning" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5" }}>
            ⚠ Ya registrado: <b>{duplicate.plates}</b> {[duplicate.brand, duplicate.model].filter(Boolean).join(" ")}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <In label="Placas *" testId="vehicle-plates" value={f.plates} onChange={(v) => setF({ ...f, plates: v.toUpperCase() })} />
          <In label="Alias (opcional)" testId="vehicle-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required={false} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <In label="Marca" testId="vehicle-brand" value={f.brand} onChange={(v) => setF({ ...f, brand: v })} required={false} />
          <In label="Modelo" testId="vehicle-model" value={f.model} onChange={(v) => setF({ ...f, model: v })} required={false} />
          <In label="Año" testId="vehicle-year" type="number" value={f.year} onChange={(v) => setF({ ...f, year: v })} required={false} />
        </div>
        <In label="Número de serie (NIV / VIN)" testId="vehicle-vin" value={f.vin} onChange={(v) => setF({ ...f, vin: v.toUpperCase() })} required={false} />
        <div className="grid grid-cols-2 gap-3">
          <In label="Chofer / responsable" testId="vehicle-driver" value={f.driver_name} onChange={(v) => setF({ ...f, driver_name: v })} required={false} />
          <Sel label="Obra asignada" testId="vehicle-project" value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })}
            options={[{ value: "", label: "— Administrativo (sin obra) —" }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
        </div>
        {vehicle && (
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-200">
            <input type="checkbox" checked={f.active} data-testid="vehicle-active" onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-cyan-400" />
            Vehículo activo
          </label>
        )}
        <p className="text-[11px] text-slate-500">💡 Los gastos aprobados se registran automáticamente como gasto de la obra asignada (o administrativo si no tiene obra), así la caja cuadra sola.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="vehicle-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FleetExpenseModal({ vehicle, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ expense_type: "gasolina", amount: "", expense_date: today, notes: "", odometer_km: "", liters: "", payment_method: "efectivo" });
  const [ticket, setTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const isFuel = f.expense_type === "gasolina";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let receipt_path = null;
      if (ticket) {
        const fd = new FormData();
        fd.append("file", ticket);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_path = data.id;
      }
      const { data } = await api.post("/fleet/expenses", {
        vehicle_id: vehicle.id,
        expense_type: f.expense_type,
        amount: Number(f.amount),
        expense_date: f.expense_date,
        notes: f.notes || null,
        odometer_km: f.odometer_km ? Number(f.odometer_km) : null,
        liters: isFuel && f.liters ? Number(f.liters) : null,
        receipt_path,
        payment_method: f.payment_method,
      });
      toast.success(data.status === "aprobado" ? "Gasto registrado y aplicado a contabilidad" : "Gasto enviado — pendiente de aprobación");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Gasto · ${vehicle?.plates || ""}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="fleet-expense-modal">
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Tipo de gasto" testId="fexp-type" value={f.expense_type} onChange={(v) => setF({ ...f, expense_type: v })} options={FLEET_TYPES} />
          <In label="Monto MXN *" testId="fexp-amount" type="number" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <In label="Fecha *" testId="fexp-date" type="date" value={f.expense_date} onChange={(v) => setF({ ...f, expense_date: v })} />
          <In label="Kilometraje (opcional)" testId="fexp-km" type="number" value={f.odometer_km} onChange={(v) => setF({ ...f, odometer_km: v })} required={false} />
          {isFuel ? (
            <In label="Litros" testId="fexp-liters" type="number" value={f.liters} onChange={(v) => setF({ ...f, liters: v })} required={false} />
          ) : (
            <div>
              <label className="label-mono block mb-1.5">Forma de pago</label>
              <select data-testid="fexp-method" value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>
          )}
        </div>
        {isFuel && (
          <div>
            <label className="label-mono block mb-1.5">Forma de pago</label>
            <select data-testid="fexp-method-fuel" value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>
        )}
        <div>
          <label className="label-mono block mb-1.5">Notas (opcional)</label>
          <input data-testid="fexp-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Ej. cambio de aceite, caseta Cuernavaca…" className="input-cyber px-3 py-2 text-sm w-full" />
        </div>
        <div>
          <label className="label-mono block mb-1.5">Ticket / foto (opcional)</label>
          <input type="file" accept="image/*,application/pdf" data-testid="fexp-ticket" onChange={(e) => setTicket(e.target.files?.[0] || null)}
            className="w-full text-xs text-slate-400 file:btn-ghost file:px-3 file:py-1.5 file:rounded-sm file:border file:text-xs file:mr-3" />
          {ticket && <p className="text-[11px] mt-1" style={{ color: "var(--cyan)" }}>📎 {ticket.name}</p>}
        </div>
        {isFuel && <p className="text-[11px] text-slate-500">💡 Captura kilometraje y litros en cada carga para calcular el rendimiento (km/L) del vehículo.</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="fexp-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Registrar
          </button>
        </div>
      </form>
    </Modal>
  );
}
